import { useState, useEffect } from 'react';
import './Tabs.css';

/**
 * Reusable Tabs component
 *
 * @param {Object} props
 * @param {Array} props.tabs - Array of tab objects: { id: string, label: string, content: ReactNode, disabled?: boolean }
 * @param {string} props.activeTab - Controlled active tab id (optional)
 * @param {function} props.onTabChange - Callback when tab changes (optional)
 * @param {string} props.defaultTab - Default active tab id (optional, defaults to first tab)
 * @param {string} props.variant - Style variant: 'default', 'config' (optional)
 * @param {string} props.className - Additional CSS class (optional)
 */
export default function Tabs({
  tabs,
  activeTab: controlledActiveTab,
  onTabChange,
  defaultTab,
  variant = 'default',
  className = ''
}) {
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab || tabs[0]?.id);

  // Support both controlled and uncontrolled modes
  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab;

  // Sync internal state when controlled prop changes
  useEffect(() => {
    if (controlledActiveTab !== undefined) {
      setInternalActiveTab(controlledActiveTab);
    }
  }, [controlledActiveTab]);

  const handleTabClick = (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    if (tab?.disabled) return;

    setInternalActiveTab(tabId);
    onTabChange?.(tabId);
  };

  const activeTabContent = tabs.find(t => t.id === activeTab)?.content;
  const variantClass = variant !== 'default' ? `tabs--${variant}` : '';

  return (
    <div className={`tabs ${variantClass} ${className}`.trim()}>
      <div className="tabs-header">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tabs-tab ${activeTab === tab.id ? 'tabs-tab-active' : ''} ${tab.disabled ? 'tabs-tab-disabled' : ''}`}
            onClick={() => handleTabClick(tab.id)}
            disabled={tab.disabled}
            type="button"
            data-sfx="button"
          >
            {tab.icon && <span className="tabs-tab-icon">{tab.icon}</span>}
            {tab.label}
          </button>
        ))}
      </div>
      {activeTabContent && (
        <div className="tabs-content">
          {activeTabContent}
        </div>
      )}
    </div>
  );
}
